/**
 * Barra OPUS del ecosistema (cambiador de apps). La MISMA dentro del hub y en el sitio de la
 * app: la pinta siempre la app, arriba del header. El botón de 9 puntos del header y el botón
 * «Ocultar barra» la muestran/ocultan; la preferencia se recuerda (localStorage
 * `opus_launcher_open` en su sitio; dentro del hub la guarda el hub para todas las apps).
 *
 * Cambiar de app NUNCA abre otra pestaña:
 *   - dentro del hub se lo pide al hub (postMessage), que hace el fundido entre marcos vivos;
 *   - en su sitio navega en esta pestaña con el ticket SSO (el adelantado al pasar el mouse).
 * «Mis aplicaciones», «Mi plan», «Soporte» y «Facturación» van al hub igual: sin pestaña nueva.
 */

import { makeStyles, mergeClasses } from '@fluentui/react-components';
import { controlIcono } from './controlDelHeader.js';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import {
  STATIC_HUB_APPS,
  enviarAlHub,
  fetchHubMe,
  fondoApp,
  hubOrigin,
  irAlHub,
  launcherWho,
  navegarAApp,
  ticketsSso,
  tileTitle,
  type HubApp,
  type HubMe,
  type HubPagina,
  type LauncherAppUser,
} from '../api/hub.js';
import { estadoDelMensaje } from '../lib/embebidoEnHub.js';

const STORAGE_KEY = 'opus_launcher_open';
const BAR_ID = 'opus-launcher-bar';

/** Id de la app que pinta el launcher (`ai`, `ci`, `bi`…); el hub puede mandar más. */
export type OpusAppId = string;
export type { LauncherAppUser };

type LauncherCtx = {
  open: boolean;
  toggle: () => void;
  currentApp: OpusAppId;
  me: HubMe;
  appUser: LauncherAppUser | null;
  openingId: string | null;
  openApp: (app: HubApp) => void;
  prefetchApp: (app: HubApp) => void;
  cancelPrefetch: () => void;
  irA: (pagina: HubPagina) => void;
};

const Ctx = createContext<LauncherCtx | null>(null);

/** Abierta por omisión: es la navegación entre apps. Solo se oculta si el cliente la ocultó. */
function readStoredOpen(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) !== '0';
  } catch {
    return true;
  }
}

function writeStoredOpen(open: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, open ? '1' : '0');
  } catch {
    /* ignore quota / private mode */
  }
}

const GUEST: HubMe = {
  apps: STATIC_HUB_APPS,
  client: null,
  ia_plan: null,
  authenticated: false,
};

export function OpusLauncherRoot({
  currentApp,
  hidden,
  enHub = false,
  appUser = null,
  children,
}: {
  currentApp: OpusAppId;
  hidden?: boolean;
  /** La app corre dentro del hub (lib/embebidoEnHub): habla con el hub por postMessage. */
  enHub?: boolean;
  /** Sesión de la app: si el hub no responde o no hay cookie del hub (p.ej. `ci.localhost` en local). */
  appUser?: LauncherAppUser | null;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(readStoredOpen);
  const [me, setMe] = useState<HubMe>(GUEST);
  const [openingId, setOpeningId] = useState<string | null>(null);
  const prefetchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (hidden) return;
    const ac = new AbortController();
    void fetchHubMe(ac.signal)
      .then((next) => setMe(next))
      .catch(() => {
        /* abort o red: se queda en GUEST / estáticas al resolver */
      });
    return () => ac.abort();
  }, [hidden]);

  // Dentro del hub: el hub manda qué app se está abriendo y si la barra va abierta.
  useEffect(() => {
    if (hidden || !enHub) return;
    const origen = hubOrigin();
    const onMsg = (ev: MessageEvent) => {
      if (!origen || ev.origin !== origen) return;
      const estado = estadoDelMensaje(ev.data);
      if (!estado) return;
      setOpeningId(estado.abriendo);
      setOpen(estado.barra);
    };
    window.addEventListener('message', onMsg);
    enviarAlHub({ tipo: 'opus-hub:hola' });
    return () => window.removeEventListener('message', onMsg);
  }, [enHub, hidden]);

  useEffect(() => () => {
    if (prefetchTimer.current) clearTimeout(prefetchTimer.current);
  }, []);

  const toggle = useCallback(() => {
    setOpen((prev) => {
      const next = !prev;
      writeStoredOpen(next);
      if (enHub) enviarAlHub({ tipo: 'opus-hub:barra', abierta: next });
      return next;
    });
  }, [enHub]);

  const cancelPrefetch = useCallback(() => {
    if (prefetchTimer.current) clearTimeout(prefetchTimer.current);
    prefetchTimer.current = null;
  }, []);

  // Ticket por adelantado (con un respiro para no pedir uno por cada tile que cruza el puntero).
  const prefetchApp = useCallback(
    (app: HubApp) => {
      if (app.id === currentApp || !app.live || !app.allowed) return;
      if (!enHub && !me.authenticated) return;
      cancelPrefetch();
      prefetchTimer.current = setTimeout(() => {
        if (enHub) enviarAlHub({ tipo: 'opus-hub:prefetch', app: app.id });
        else void ticketsSso.prefetch(app.id);
      }, 90);
    },
    [cancelPrefetch, currentApp, enHub, me.authenticated],
  );

  const openApp = useCallback(
    async (app: HubApp) => {
      if (app.id === currentApp) return;
      if (!app.live || !app.allowed) return;
      if (openingId) return;
      if (enHub) {
        // El hub hace el cambio (fundido entre marcos) y nos avisa del progreso.
        setOpeningId(app.id);
        enviarAlHub({ tipo: 'opus-hub:abrir', app: app.id });
        return;
      }
      setOpeningId(app.id);
      // Con sesión del hub usa el ticket SSO (el adelantado si hay); sin ella, la URL directa.
      const fue = await navegarAApp(async () => (me.authenticated ? await ticketsSso.tomar(app.id) : null) ?? app.url);
      if (!fue) setOpeningId(null);
    },
    [currentApp, enHub, me.authenticated, openingId],
  );

  const irA = useCallback((pagina: HubPagina) => irAlHub(pagina, enHub), [enHub]);

  const value = useMemo<LauncherCtx>(
    () => ({ open, toggle, currentApp, me, appUser, openingId, openApp, prefetchApp, cancelPrefetch, irA }),
    [open, toggle, currentApp, me, appUser, openingId, openApp, prefetchApp, cancelPrefetch, irA],
  );

  if (hidden) return <>{children}</>;
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

function useLauncher(): LauncherCtx | null {
  return useContext(Ctx);
}

function GridDotsIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
      {[0, 1, 2].flatMap((row) =>
        [0, 1, 2].map((col) => (
          <circle
            key={`${row}-${col}`}
            cx={3 + col * 5}
            cy={3 + row * 5}
            r="1.35"
            fill="currentColor"
          />
        )),
      )}
    </svg>
  );
}

/**
 * Símbolo de ECOSOFT (decorativo, sin función: para volver está «Mis aplicaciones»). Es el
 * logo real (public/ecosoft-logo.png, el mismo `ecosoft.png` del hub); en claro lo oscurece
 * `--ecosoft-mark-filter` para que no se pierda contra el blanco.
 */
function EcosoftMark({ className, imgClassName }: { className?: string; imgClassName?: string }) {
  return (
    <span className={className} title="ECOSOFT" aria-hidden="true">
      <img src="/ecosoft-logo.png" alt="" aria-hidden="true" width={22} height={22} className={imgClassName} draggable={false} />
    </span>
  );
}

function ChevronUpIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 10l4-4 4 4" />
    </svg>
  );
}

/*
 * MEDIDAS COMPARTIDAS con OPUS bi (frontend/src/components/OpusLauncher.tsx) y con la barra
 * del esqueleto del hub (opus.com.mx/public/app.html, `.eskel .sk-bar`): si cambias una,
 * cámbiala allá también. Barra 56px; padding lateral 24px (16px en celular); tile 36px
 * (radio 10px) con ícono de 28px en degradado del color de la app (solo la sigla); actual =
 * fondo hover + anillo de 2px del color; cargando = anillo que gira; enlaces 12px/600;
 * a la derecha «Mis aplicaciones» y el botón de ocultar (34px).
 */
const MOVIL = '@media (max-width: 639px)';
const SIN_MOVIMIENTO = '@media (prefers-reduced-motion: reduce)';

const useStyles = makeStyles({
  /** 9 puntos del header: mismo control cuadrado de 38px que el tema y el carrito. */
  trigger: controlIcono,
  triggerOpen: {
    background: 'var(--accent-weak)',
    border: '1px solid var(--accent)',
    color: 'var(--accent)',
    ':hover': { background: 'var(--accent-weak)', border: '1px solid var(--accent)', color: 'var(--accent)' },
  },
  bar: {
    overflow: 'hidden',
    maxHeight: '0px',
    opacity: 0,
    // Cerrada queda fuera del orden de tabulación (visibility) DESPUÉS de plegarse.
    transitionProperty: 'max-height, opacity, visibility',
    transitionDuration: '200ms, 200ms, 0s',
    transitionDelay: '0s, 0s, 200ms',
    transitionTimingFunction: 'ease',
    // Cerrada no deja ni la línea de 1px del borde arriba del header.
    borderBottom: '0 solid transparent',
    // Mismo fondo que el header: abierta se lee como la parte de arriba del MISMO bloque.
    backgroundColor: 'var(--bg-header)',
    pointerEvents: 'none',
    boxSizing: 'border-box',
    visibility: 'hidden',
    [SIN_MOVIMIENTO]: { transitionDuration: '0s, 0s, 0s', transitionDelay: '0s, 0s, 0s' },
  },
  barOpen: {
    maxHeight: '57px',
    opacity: 1,
    visibility: 'visible',
    transitionDelay: '0s, 0s, 0s',
    borderBottom: '1px solid var(--border-subtle)',
    pointerEvents: 'auto',
  },
  inner: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    height: '56px',
    padding: '0 var(--space-xxl)',
    boxSizing: 'border-box',
    minWidth: 0,
    [MOVIL]: { padding: '0 16px', gap: '8px' },
  },
  eco: {
    width: '28px',
    height: '28px',
    flexShrink: 0,
    display: 'grid',
    placeItems: 'center',
    color: 'var(--text-tertiary)',
    // Alineado con el botón de 9 puntos (38px) del header de abajo.
    marginLeft: '5px',
    marginRight: '5px',
  },
  ecoImg: {
    display: 'block',
    width: '22px',
    height: '22px',
    filter: 'var(--ecosoft-mark-filter)',
    userSelect: 'none',
  },
  sep: {
    width: '1px',
    height: '20px',
    flexShrink: 0,
    background: 'var(--border-strong)',
    opacity: 0.7,
    [MOVIL]: { display: 'none' },
  },
  tiles: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    overflowX: 'auto',
    minWidth: 0,
    flex: '1 1 auto',
    // 2px de aire para que el anillo de la app actual no lo recorte el overflow.
    padding: '2px',
    // Sin `scrollbarWidth`: en Chrome esa propiedad anula los `::-webkit-scrollbar-*`
    // del design system y esta fila se quedaba con la barra gris del sistema.
    [MOVIL]: { flex: '0 0 auto' },
  },
  tile: {
    position: 'relative',
    width: '36px',
    height: '36px',
    flexShrink: 0,
    display: 'grid',
    placeItems: 'center',
    padding: 0,
    border: 'none',
    background: 'transparent',
    borderRadius: '10px',
    cursor: 'pointer',
    transitionProperty: 'background, box-shadow',
    transitionDuration: '120ms',
    ':hover': { background: 'var(--bg-hover)' },
    ':hover > span': { transform: 'translateY(-1px)' },
    ':focus-visible': { outline: '2px solid var(--accent)', outlineOffset: '2px' },
  },
  tileActive: {
    background: 'var(--bg-hover)',
    boxShadow: 'inset 0 0 0 2px var(--opus-app-color)',
    cursor: 'default',
    ':hover > span': { transform: 'none' },
  },
  tileDisabled: {
    opacity: 0.4,
    cursor: 'not-allowed',
    ':hover': { background: 'transparent' },
    ':hover > span': { transform: 'none' },
  },
  /** Abriendo: anillo del color de la app que gira alrededor del tile. */
  tileBusy: {
    cursor: 'progress',
    '::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      borderRadius: '10px',
      border: '2px solid transparent',
      borderTopColor: 'var(--opus-app-color)',
      borderRightColor: 'var(--opus-app-color)',
      animationName: { to: { transform: 'rotate(360deg)' } },
      animationDuration: '700ms',
      animationIterationCount: 'infinite',
      animationTimingFunction: 'linear',
      [SIN_MOVIMIENTO]: { animationName: 'none', border: '2px solid var(--opus-app-color)', opacity: 0.6 },
    },
  },
  ico: {
    width: '28px',
    height: '28px',
    borderRadius: '7px',
    display: 'grid',
    placeItems: 'center',
    color: '#fff',
    fontFamily: 'var(--font-display)',
    fontSize: '12px',
    fontWeight: 700,
    lineHeight: 1,
    letterSpacing: '0.01em',
    transitionProperty: 'transform',
    transitionDuration: '120ms',
  },
  /** Barra de progreso fina pegada arriba de la página mientras se abre otra app. */
  progreso: {
    position: 'fixed',
    top: 0,
    left: 0,
    height: '2px',
    width: '72%',
    zIndex: 1000,
    pointerEvents: 'none',
    animationName: { from: { width: '0%' }, to: { width: '72%' } },
    animationDuration: '1600ms',
    animationTimingFunction: 'cubic-bezier(.1,.7,.2,1)',
    [SIN_MOVIMIENTO]: { animationName: 'none' },
  },
  links: {
    display: 'flex',
    alignItems: 'center',
    gap: '2px',
    flexShrink: 0,
    [MOVIL]: { flex: '1 1 auto', minWidth: 0, overflowX: 'auto' },
  },
  linkBtn: {
    border: 'none',
    background: 'transparent',
    color: 'var(--text-secondary)',
    font: 'inherit',
    fontSize: '12px',
    fontWeight: 600,
    lineHeight: '16px',
    padding: '6px 8px',
    borderRadius: 'var(--radius-control)',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    ':hover': { background: 'var(--bg-hover)', color: 'var(--text-primary)' },
    ':focus-visible': { outline: '2px solid var(--accent)', outlineOffset: '2px' },
  },
  right: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginLeft: 'auto',
    flexShrink: 0,
  },
  who: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    minWidth: 0,
    maxWidth: '180px',
    lineHeight: 1.2,
    [MOVIL]: { display: 'none' },
  },
  whoName: {
    fontSize: '12px',
    fontWeight: 600,
    color: 'var(--text-primary)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '180px',
  },
  whoMail: {
    fontSize: '10px',
    color: 'var(--text-tertiary)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '180px',
  },
  loginBtn: {
    border: '1px solid var(--control-border)',
    background: 'transparent',
    color: 'var(--text-primary)',
    font: 'inherit',
    fontSize: '12px',
    fontWeight: 600,
    lineHeight: '16px',
    padding: '5px 10px',
    borderRadius: 'var(--radius-control)',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    ':hover': { background: 'var(--bg-hover)' },
  },
  hubBtn: {
    border: 'none',
    background: 'var(--accent-weak)',
    color: 'var(--accent)',
    font: 'inherit',
    fontSize: '12px',
    fontWeight: 700,
    lineHeight: '16px',
    padding: '6px 12px',
    borderRadius: 'var(--radius-control)',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    ':hover': { filter: 'brightness(1.08)' },
    ':focus-visible': { outline: '2px solid var(--accent)', outlineOffset: '2px' },
  },
  /** En celular «Inicia sesión en el hub» ya lleva al hub: no cabe el segundo botón. */
  hubBtnConLogin: {
    [MOVIL]: { display: 'none' },
  },
  hubIco: {
    display: 'inline-block',
    verticalAlign: '-3px',
    marginRight: '6px',
    [MOVIL]: { marginRight: 0 },
  },
  /** En celular «Mis aplicaciones» queda solo con su ícono (si no, el botón de ocultar se sale). */
  hubTxt: {
    [MOVIL]: { display: 'none' },
  },
  hideBtn: {
    width: '34px',
    height: '34px',
    flexShrink: 0,
    display: 'grid',
    placeItems: 'center',
    padding: 0,
    border: 'none',
    background: 'transparent',
    color: 'var(--text-secondary)',
    borderRadius: 'var(--radius-control)',
    cursor: 'pointer',
    ':hover': { background: 'var(--bg-hover)', color: 'var(--text-primary)' },
    ':focus-visible': { outline: '2px solid var(--accent)', outlineOffset: '2px' },
  },
});

export function OpusLauncherButton() {
  const ctx = useLauncher();
  const styles = useStyles();
  if (!ctx) return null;
  return (
    <button
      type="button"
      className={mergeClasses(styles.trigger, ctx.open && styles.triggerOpen)}
      aria-label="Aplicaciones OPUS"
      title="Aplicaciones OPUS"
      aria-expanded={ctx.open}
      aria-controls={BAR_ID}
      onClick={ctx.toggle}
    >
      <GridDotsIcon />
    </button>
  );
}

export function OpusLauncherBar() {
  const ctx = useLauncher();
  const styles = useStyles();
  if (!ctx) return null;
  const { open, toggle, me, appUser, currentApp, openingId, openApp, prefetchApp, cancelPrefetch, irA } = ctx;
  const who = launcherWho(me, appUser);
  const abriendo = openingId ? me.apps.find((a) => a.id === openingId) : null;

  return (
    <>
      {abriendo ? (
        <div className={styles.progreso} style={{ background: abriendo.color }} role="progressbar" aria-label={`Abriendo ${abriendo.label}`} />
      ) : null}
      <div
        id={BAR_ID}
        className={mergeClasses(styles.bar, open && styles.barOpen)}
        role="region"
        aria-label="Aplicaciones del ecosistema OPUS"
        aria-hidden={!open}
      >
        <div className={styles.inner}>
          <EcosoftMark className={styles.eco} imgClassName={styles.ecoImg} />
          <span className={styles.sep} aria-hidden="true" />
          <div className={styles.tiles}>
            {me.apps.map((app) => {
              const current = app.id === currentApp;
              const disabled = !current && (!app.live || !app.allowed);
              const busy = openingId === app.id;
              const title = busy ? `Abriendo ${app.label}…` : tileTitle(app, current);
              return (
                <button
                  key={app.id}
                  type="button"
                  className={mergeClasses(
                    styles.tile,
                    current && styles.tileActive,
                    disabled && styles.tileDisabled,
                    busy && styles.tileBusy,
                  )}
                  style={{ '--opus-app-color': app.color } as CSSProperties}
                  disabled={disabled}
                  aria-label={title}
                  aria-current={current ? 'true' : undefined}
                  aria-busy={busy || undefined}
                  title={title}
                  onPointerEnter={() => prefetchApp(app)}
                  onPointerLeave={cancelPrefetch}
                  onFocus={() => prefetchApp(app)}
                  onClick={() => void openApp(app)}
                >
                  <span className={styles.ico} style={{ background: fondoApp(app.color) }} aria-hidden="true">
                    {app.id.slice(0, 2)}
                  </span>
                </button>
              );
            })}
          </div>

          <div className={styles.links}>
            <button type="button" className={styles.linkBtn} onClick={() => irA('plan')}>
              Mi plan
            </button>
            <button type="button" className={styles.linkBtn} onClick={() => irA('soporte')}>
              Soporte
            </button>
            <button type="button" className={styles.linkBtn} onClick={() => irA('facturacion')}>
              Facturación
            </button>
          </div>

          <div className={styles.right}>
            {who.label ? (
              <div className={styles.who} title={who.mail ? `${who.label} · ${who.mail}` : who.label}>
                <span className={styles.whoName}>{who.label}</span>
                {who.mail ? <span className={styles.whoMail}>{who.mail}</span> : null}
              </div>
            ) : null}
            {who.showLogin ? (
              <button type="button" className={styles.loginBtn} onClick={() => irA('home')}>
                Inicia sesión en el hub
              </button>
            ) : null}
            <button
              type="button"
              className={mergeClasses(styles.hubBtn, who.showLogin && styles.hubBtnConLogin)}
              title="Volver a Mis aplicaciones"
              aria-label="Volver a Mis aplicaciones"
              onClick={() => irA('home')}
            >
              <span className={styles.hubIco}><GridDotsIcon /></span>
              <span className={styles.hubTxt}>Mis aplicaciones</span>
            </button>
            <button
              type="button"
              className={styles.hideBtn}
              title="Ocultar barra (el botón de 9 puntos la vuelve a mostrar)"
              aria-label="Ocultar barra de aplicaciones"
              aria-controls={BAR_ID}
              onClick={toggle}
            >
              <ChevronUpIcon />
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
